import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { 
  ClipboardList, Map, FileText, ArrowRight, 
  LogOut, CheckCircle, Lock, Sparkles, User
} from "lucide-react";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { PlanAvailabilityDialog } from "@/components/PlanAvailabilityDialog";
import { canUserAccessPlanCheckout } from "@/lib/planCheckoutAccess";
import { trackGaEvent } from "@/lib/ga4";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPlanAvailabilityDialog, setShowPlanAvailabilityDialog] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`, { withCredentials: true });
      setDashboardData(response.data);
    } catch (error) {
      console.error("Dashboard error:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/", { replace: true });
    }
  };

  const handleUpgrade = async () => {
    const canAccessCheckout = canUserAccessPlanCheckout(user);
    trackGaEvent("plan_unlock_clicked", {
      source: "dashboard",
      checkout_allowed: canAccessCheckout,
    });

    if (!canAccessCheckout) {
      setShowPlanAvailabilityDialog(true);
      return;
    }

    try {
      const response = await axios.post(
        `${API}/payments/checkout`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      trackGaEvent("checkout_started", {
        source: "dashboard",
        session_id: response.data.session_id,
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Checkout error:", error);
      if (error.response?.status === 403 && error.response?.data?.detail?.code === "plans_launch_pending") {
        setShowPlanAvailabilityDialog(true);
        return;
      }
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    }
  };

  const planAccess = dashboardData?.user?.plan_access || user?.plan_access || {};

  const journeySteps = [
    {
      id: "intake",
      title: "Complete Assessment",
      description: "Tell us about your goals and constraints",
      icon: <ClipboardList className="w-5 h-5" />,
      path: "/assessment",
      completed: dashboardData?.intake_completed,
      available: true,
      isFreeJourney: true
    },
    {
      id: "orientation",
      title: "Your Assessment Results",
      description: "See countries aligned with your priorities",
      icon: <Map className="w-5 h-5" />,
      path: "/assessment-results",
      completed: dashboardData?.intake_completed, // Orientation is complete when results exist (intake done = results ready)
      available: dashboardData?.intake_completed,
      isFreeJourney: true
    }
  ];

  const nextSteps = [
    {
      id: "plan",
      title: "Full Relocation Blueprint",
      description: "Your personalized roadmap",
      icon: <FileText className="w-5 h-5" />,
      path: "/plan",
      completed: dashboardData?.has_plan,
      available: dashboardData?.user?.has_paid,
      locked: !dashboardData?.user?.has_paid,
      isOptional: true
    }
  ];

  // Free journey progress: only intake + orientation
  const freeJourneyComplete = dashboardData?.intake_completed;
  const progress = freeJourneyComplete ? 100 : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header>
        <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 text-sm text-stone-300/85">
                <User className="w-4 h-4" />
                {dashboardData?.user?.name || user?.name}
              </div>
              <Button 
                data-testid="logout-btn"
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="rounded-full border border-stone-700 bg-stone-900/80 px-4 py-2 text-stone-300 transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-amber-400/45 hover:bg-stone-900 hover:text-stone-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
        </div>
      </Header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Welcome Section */}
        <div className="mb-10 opacity-0 animate-fade-in-up">
          <h1 className="font-manrope font-bold text-3xl md:text-4xl text-foreground mb-2">
            Welcome, {dashboardData?.user?.name?.split(' ')[0] || 'there'}
          </h1>
          <p className="text-muted-foreground text-lg">
            {freeJourneyComplete 
              ? dashboardData?.user?.has_paid
                ? planAccess?.requires_new_purchase_for_refresh
                  ? "You've updated your assessment beyond the included refresh. Review your current Blueprint, or purchase a refreshed one when you're ready."
                  : "You've completed your assessment and Blueprint. Review your results or update your preferences to explore other options."
                : "You've completed your assessment. Review your results or adjust preferences to explore alternatives."
              : "Let's help you think through your international relocation"
            }
          </p>
        </div>

        {/* Progress Overview */}
        <Card className="mb-8 opacity-0 animate-fade-in-up animation-delay-100">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-manrope text-lg">Your Progress</CardTitle>
              <span className="text-sm text-muted-foreground">
                {dashboardData?.has_plan ? "Assessment and Blueprint complete" : freeJourneyComplete ? "Assessment complete" : "Getting started"}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <Progress value={progress} className="h-2" />
          </CardContent>
        </Card>

        {/* Next Step CTA (if not paid and orientation complete) */}
        {!dashboardData?.user?.has_paid && dashboardData?.intake_completed && (
          <Card className="hover-contrast-surface mb-8 border-accent/50 bg-accent/5 opacity-0 animate-fade-in-up animation-delay-200" data-testid="dashboard-upgrade-cta-card">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="hover-contrast-chip w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="hover-contrast-icon w-6 h-6 text-accent" />
                </div>
                <div className="flex-1">
                  <h3 className="hover-contrast-text font-manrope font-bold text-xl text-foreground mb-3">
                    Ready to Go Deeper?
                  </h3>
                  <p className="hover-contrast-text-muted text-muted-foreground mb-4 leading-relaxed">
                    You&apos;ve completed your free assessment and have clarity on which countries align with your goals.
                  </p>
                  <p className="hover-contrast-text-muted text-muted-foreground mb-4 leading-relaxed">
                    If you&apos;d like to continue with a more detailed Blueprint, your full Relocation Blueprint includes:
                  </p>
                  <ul className="space-y-2 mb-4 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="hover-contrast-icon w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                      <span className="hover-contrast-text-muted">A short-list of countries that realistically fit you</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="hover-contrast-icon w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                      <span className="hover-contrast-text-muted">High-level explanations of long-stay residency pathways</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="hover-contrast-icon w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                      <span className="hover-contrast-text-muted">Cost and lifestyle considerations to avoid surprises</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="hover-contrast-icon w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                      <span className="hover-contrast-text-muted">A step-by-step planning timeline (Now / Next / Later)</span>
                    </li>
                  </ul>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <Button 
                      data-testid="upgrade-btn"
                      onClick={handleUpgrade}
                      className="hover-contrast-button-solid bg-accent hover:bg-accent/90 text-accent-foreground rounded-full px-6"
                    >
                      Get Full Relocation Blueprint
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                    <span className="hover-contrast-text text-sm font-medium text-foreground">$29 · One-time</span>
                  </div>
                  <p className="hover-contrast-text-muted text-sm text-muted-foreground mt-4" data-testid="dashboard-upgrade-refresh-policy-copy">
                    Your paid Blueprint is based on your current assessment and includes one free refresh if you update your answers later.
                  </p>
                  <p className="hover-contrast-text-muted text-xs text-muted-foreground mt-4">
                    Planning guidance only — not legal or tax advice.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {dashboardData?.user?.has_paid && planAccess?.requires_new_purchase_for_refresh && (
          <Card className="mb-8 border-stone-200 bg-stone-50/80 opacity-0 animate-fade-in-up animation-delay-200" data-testid="dashboard-refresh-purchase-card">
            <CardContent className="p-6">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="max-w-2xl">
                  <h3 className="font-manrope text-xl text-stone-900">Need a refreshed paid Blueprint?</h3>
                  <p className="mt-3 text-sm leading-7 text-stone-600">
                    Your current Blueprint is still available, but it&apos;s tied to an earlier assessment. You&apos;ve already used the included refresh, so another refreshed Blueprint now requires a new purchase.
                  </p>
                </div>
                <Button data-testid="dashboard-refresh-plan-btn" onClick={handleUpgrade} className="rounded-full bg-stone-900 px-6 text-white hover:bg-orange-600">
                  Purchase Refreshed Blueprint
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Free Journey Steps */}
        <div className="mb-8">
          <h2 className="font-manrope font-semibold text-lg text-foreground mb-4">Free Assessment</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {journeySteps.map((step, index) => (
              <Card 
                key={step.id}
                data-testid={`step-card-${step.id}`}
                className={`relative overflow-hidden transition-colors duration-300 opacity-0 animate-fade-in-up ${
                  step.completed 
                    ? 'border-primary/30 bg-primary/5' 
                    : 'border-border hover:border-primary/30'
                }`}
                style={{ animationDelay: `${(index + 3) * 100}ms` }}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      step.completed 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-primary/10 text-primary'
                    }`}>
                      {step.completed ? <CheckCircle className="w-5 h-5" /> : step.icon}
                    </div>
                    {step.completed && (
                      <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                        Completed
                      </span>
                    )}
                  </div>
                  <CardTitle className="font-manrope text-lg mt-4">{step.title}</CardTitle>
                  <CardDescription>{step.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    data-testid={`${step.id}-action-btn`}
                    onClick={() => navigate(step.path)}
                    disabled={!step.available}
                    variant={step.completed ? "outline" : "default"}
                    className={`w-full rounded-full ${!step.completed ? 'bg-primary hover:bg-primary/90' : ''}`}
                  >
                    {step.completed ? 'Review' : step.available ? 'Start' : 'Complete previous step'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Next Steps (Optional) */}
        {dashboardData?.intake_completed && (
          <div className="mb-8">
            <h2 className="font-manrope font-semibold text-lg text-foreground mb-2">Next Steps</h2>
            <p className="text-sm text-muted-foreground mb-4">Optional ways to continue your journey</p>
            <div className="grid md:grid-cols-2 gap-6">
              {nextSteps.map((step, index) => (
                <Card 
                  key={step.id}
                  data-testid={`step-card-${step.id}`}
                  className={`relative overflow-hidden transition-colors duration-300 opacity-0 animate-fade-in-up ${
                    step.completed 
                      ? 'border-primary/30 bg-primary/5' 
                      : step.locked 
                        ? 'border-border bg-muted/30' 
                        : 'border-border hover:border-primary/30'
                  }`}
                  style={{ animationDelay: `${(index + 5) * 100}ms` }}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        step.completed 
                          ? 'bg-primary text-primary-foreground' 
                          : step.locked 
                            ? 'bg-muted text-muted-foreground' 
                            : 'bg-primary/10 text-primary'
                      }`}>
                        {step.completed ? <CheckCircle className="w-5 h-5" /> : step.locked ? <Lock className="w-5 h-5" /> : step.icon}
                      </div>
                      {step.completed && (
                        <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                          Completed
                        </span>
                      )}
                      {step.locked && (
                        <span className="px-2 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-full">
                          Premium
                        </span>
                      )}
                      {step.isOptional && !step.locked && !step.completed && (
                        <span className="px-2 py-1 bg-secondary text-secondary-foreground text-xs font-medium rounded-full">
                          Optional
                        </span>
                      )}
                    </div>
                    <CardTitle className="font-manrope text-lg mt-4">{step.title}</CardTitle>
                    <CardDescription>{step.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {step.locked ? (
                      <Button 
                        data-testid={`${step.id}-upgrade-btn`}
                        onClick={handleUpgrade}
                        className="w-full rounded-full bg-accent hover:bg-accent/90 text-accent-foreground"
                      >
                        Get Full Blueprint · $29
                      </Button>
                    ) : (
                      <Button 
                        data-testid={`${step.id}-action-btn`}
                        onClick={() => navigate(step.path)}
                        disabled={!step.available}
                        variant={step.completed ? "outline" : "default"}
                        className={`w-full rounded-full ${!step.completed ? 'bg-primary hover:bg-primary/90' : ''}`}
                      >
                        {step.completed ? 'Review' : 'Continue'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        {dashboardData?.intake_completed && (
          <div className="mt-10 pt-8 border-t border-border">
            <h2 className="font-manrope font-semibold text-xl text-foreground mb-2">Quick Actions</h2>
            <p className="text-sm text-muted-foreground mb-4">
              {dashboardData?.user?.has_paid
                ? planAccess?.requires_new_purchase_for_refresh
                  ? "Updating your assessment will refresh your free results. A new paid purchase is now required if you want refreshed paid guidance too."
                  : "Updating your assessment will refresh your free results. Your paid Relocation Blueprint also includes one free refresh if you refine your answers."
                : "Updating preferences refreshes your assessment results."}
            </p>
            <div className="flex flex-wrap gap-3">
              <Button 
                data-testid="quick-orientation-btn"
                variant="outline" 
                className="rounded-full"
                onClick={() => navigate('/assessment-results')}
              >
                <Map className="w-4 h-4 mr-2" />
                View Your Assessment Results
              </Button>
              <Button 
                data-testid="quick-preferences-btn"
                variant="outline" 
                className="rounded-full"
                onClick={() => navigate('/assessment?step=review')}
              >
                <ClipboardList className="w-4 h-4 mr-2" />
                Update Assessment
              </Button>
              {dashboardData?.user?.has_paid ? (
                <Button
                  data-testid="quick-plan-btn"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => navigate('/plan')}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View Current Blueprint
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </main>

      <Footer />
      <PlanAvailabilityDialog open={showPlanAvailabilityDialog} onOpenChange={setShowPlanAvailabilityDialog} />
    </div>
  );
};

export default Dashboard;
